import { useState, type FormEvent } from "react";
import type { Topic } from "@/mocks/topics";

type Comment = Topic["comments"][number];

export default function CommentSection({ initialComments }: { initialComments: Comment[] }) {
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [openReplies, setOpenReplies] = useState<Record<string, boolean>>({});
  const [draft, setDraft] = useState("");
  const [notice, setNotice] = useState("");

  function toggleLike(id: string) {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const text = draft.trim();
    if (!text) {
      setNotice("先写点什么再发表吧。");
      return;
    }
    if (text.length > 200) {
      setNotice("评论最多 200 字,精简一下再发。");
      return;
    }
    setComments((prev) => [
      { id: `local-${Date.now()}`, user: "我", avatarText: "我", time: "刚刚", text, like: 0 },
      ...prev,
    ]);
    setDraft("");
    setNotice("已发表,你的观点已经出现在讨论区顶部。");
  }

  return (
    <section id="comments" className="rounded-lg bg-background-100 border border-background-200/80 p-5 md:p-7">
      <div className="flex items-center gap-2 mb-5">
        <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
          <i className="ri-chat-3-line"></i>
        </span>
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">评论区</h2>
        <span className="ml-auto text-xs md:text-sm text-foreground-500">{comments.length} 条讨论</span>
      </div>

      <form onSubmit={submit} className="mb-6">
        <label className="block">
          <span className="sr-only">发表评论</span>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, 200))}
            rows={3}
            maxLength={200}
            placeholder="说说你的看法,理性讨论更容易被点赞。"
            className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
          ></textarea>
        </label>
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-foreground-400">{draft.length}/200</span>
          <button
            type="submit"
            className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center gap-2"
          >
            <i className="ri-send-plane-fill"></i>
            发表观点
          </button>
        </div>
        {notice && <p className="text-xs text-secondary-700 mt-2 flex items-center gap-1.5"><i className="ri-information-line"></i>{notice}</p>}
      </form>

      <ul className="flex flex-col gap-5">
        {comments.map((c) => (
          <li key={c.id} className="flex gap-3 md:gap-4">
            <span className="w-9 h-9 md:w-10 md:h-10 shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-900 text-sm font-medium">
              {c.avatarText}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-foreground-900">{c.user}</span>
                <span className="text-xs text-foreground-400">{c.time}</span>
              </div>
              <p className="text-sm text-foreground-700 mt-1.5 leading-relaxed">{c.text}</p>
              <div className="flex items-center gap-4 mt-2.5">
                <button
                  type="button"
                  onClick={() => toggleLike(c.id)}
                  className={`flex items-center gap-1.5 text-xs transition-colors cursor-pointer ${liked[c.id] ? "text-primary-600" : "text-foreground-500 hover:text-primary-600"}`}
                >
                  <i className={liked[c.id] ? "ri-thumb-up-fill" : "ri-thumb-up-line"}></i>
                  {c.like + (liked[c.id] ? 1 : 0)}
                </button>
                {c.replies && c.replies.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setOpenReplies((p) => ({ ...p, [c.id]: !p[c.id] }))}
                    className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer flex items-center gap-1"
                  >
                    <i className="ri-chat-forward-line"></i>
                    {openReplies[c.id] ? "收起回复" : `${c.replies.length} 条回复`}
                  </button>
                )}
              </div>

              {c.replies && openReplies[c.id] && (
                <div className="mt-3 pl-3 border-l-2 border-background-300 flex flex-col gap-3">
                  {c.replies.map((r, i) => (
                    <div key={i} className="text-sm">
                      <span className="font-medium text-foreground-800">{r.user}：</span>
                      <span className="text-foreground-600">{r.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}